import React, { useEffect, useRef, useContext, useCallback } from 'react';
import { EditorView } from '@annote/core/components/proseEditor';
import { Note } from '@annote/types';
import styled from 'styled-components';
import { colors, text } from '@annote/core/styles';
import { FocusContext } from 'src/context/FocusContext';
import { suggestLinkMenu } from '@annote/core/components/proseEditor/suggestLinkMenu';
import { useEditorContext } from 'src/context/EditorContext';
import { suggestionStyles } from '@annote/core/components/proseEditor/suggestionStyles';

const EditorContainer = styled.span<{
  isInline: boolean;
  placeholder: string;
  focusedPlaceholder: string;
}>`
  ${({ isInline }) =>
    !isInline &&
    `
      display: flex;
      flex-direction: column;
      height: 100%; // Take full height of parent
      flex: 1; // Add this to make it grow within flex parents
      width: 100%; // Ensure full width
      min-height: 100%; // Ensure it fills container
      position: relative;
    `}

  .ProseMirror ul {
    margin-block-start: -1em;
    margin-block-end: 0em;
    padding-inline-start: 20px;
    list-style-type: disc;
  }

  .ProseMirror {
    outline: none;
    white-space: pre-wrap; /* Preserve spaces and line breaks */
    word-wrap: break-word; /* Allow long words to be broken */
    ${({ isInline }) =>
      !isInline &&
      `
          flex: 1; // Fill remaining space
          min-height: 100%; // Ensure it fills container
        `}
  }

  .ProseMirror.read-only {
    cursor: default;
    pointer-events: none;
  }

  .ProseMirror p:first-child:last-child:has(br:only-child) {
    position: relative;
  }

  .ProseMirror p:first-child:last-child:has(br:only-child):after {
    content: ${({ placeholder }) => `"${placeholder}"`};
    font-family: ${text.family.primary};
    font-size: ${text.size.primary};
    font-weight: ${text.weight.regular};
    color: ${colors.text.placeholder};
    pointer-events: none;
    position: absolute;
    left: 0;
    top: 0;
  }

  .ProseMirror.ProseMirror-focused p:first-child:last-child:has(br:only-child):after {
    content: ${({ focusedPlaceholder }) => `"${focusedPlaceholder}"`};
  }

  // Styles for suggestion-mode
  ${suggestionStyles}
`;

export const ProseEditor = React.memo(
  ({
    note,
    placeholder = 'placeholder',
    focusedPlaceholder = 'focused placeholder',
    isReadOnly = false,
    isInline = true,
  }: {
    note: Note;
    placeholder?: string;
    focusedPlaceholder?: string;
    isReadOnly?: boolean;
    isInline?: boolean;
  }) => {
    const editorRef = useRef<HTMLDivElement>(null);
    const editorViewRef = useRef<EditorView | null>(null);
    const { focusedNoteId, focusedTarget } = useContext(FocusContext);
    const { getOrCreateEditor, removeEditor, saveEditorContent } = useEditorContext();

    // Use a stable reference to the note id
    const noteId = note.id;

    const handleBlur = useCallback(async (): Promise<void> => {
      if (!editorViewRef.current) return;
      if (suggestLinkMenu.recentlyClicked) return; // not a real blur if they clicked on a suggestion

      // Only save content for editable editors
      if (!isReadOnly) {
        await saveEditorContent(noteId);
      }
    }, [noteId, saveEditorContent, isReadOnly]);

    useEffect((): (() => void) | undefined => {
      // Only proceed when we have a DOM element to attach to
      if (!editorRef.current) return undefined;

      // Don't recreate the editor view if it already exists
      if (editorViewRef.current) return undefined;

      // Use our centralized editor management
      const enableSuggestions = !isReadOnly; // No need for suggestions in readonly mode
      editorViewRef.current = getOrCreateEditor(editorRef.current, note, isReadOnly, enableSuggestions);

      // Cleanup on component unmount
      return () => {
        if (isReadOnly) {
          // For readonly views, we need to manually destroy the editor
          // since it's not tracked in the context
          if (editorViewRef.current) {
            editorViewRef.current.destroy();
          }
        } else {
          // For editable views, use the context's removeEditor
          removeEditor(noteId);
        }
        editorViewRef.current = null;
      };
    }, [getOrCreateEditor, removeEditor, noteId, isReadOnly, note]);

    // Handle Focus changes
    useEffect(() => {
      if (isReadOnly) return; // Skip for readonly editors
      if (!editorViewRef.current || !editorRef.current) return;
      if (focusedNoteId !== noteId) return;

      // By default, focus the editor unless they've explicitly clicked on a different target
      if (focusedTarget && !(editorRef.current as HTMLElement).contains(focusedTarget)) return;

      editorViewRef.current.focus();
    }, [focusedNoteId, noteId, focusedTarget, isReadOnly]);

    return (
      <EditorContainer
        ref={editorRef}
        onBlur={handleBlur}
        isInline={isInline}
        placeholder={isReadOnly ? '' : placeholder}
        focusedPlaceholder={isReadOnly ? '' : focusedPlaceholder}
      />
    );
  },
  (prev, next) => prev.note.id === next.note.id && prev.isReadOnly === next.isReadOnly,
);
